import { AnimatePresence, motion } from 'framer-motion';
import { Component, lazy, Suspense, useEffect, type PropsWithChildren, type ReactNode } from 'react';

import { QBox, QButton, QLoader, QStack, QText } from '@captivateiq/quotient';

import { AIShellContextProvider, useAIShellContext } from './AIShellContext';
import type { APIService, IContextDetection, UserService } from './interfaces/services';

const AIShell = lazy(() => import('./components/AIShell').then((m) => ({ default: m.AIShell })));

class AIShellErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  state: { error: Error | null } = { error: null };

  render() {
    if (this.state.error) {
      return (
        <QStack spacing={2} sx={{ height: '100%', alignItems: 'center', justifyContent: 'center' }}>
          <QText variant="mediumSubheading">Failed to load the assistant</QText>
          <QButton emphasis="medium" onClick={() => this.setState({ error: null })}>
            Try again
          </QButton>
        </QStack>
      );
    }
    return this.props.children;
  }
}

const COLLAPSED_WIDTH_PX = 444;
// Full viewport width when shell is expanded (vw units).
const EXPANDED_WIDTH_VW = 100;

const AIShellAnimated = () => {
  const { isOpen, isExpanded } = useAIShellContext();
  const targetWidth = isExpanded ? `${EXPANDED_WIDTH_VW}vw` : `${COLLAPSED_WIDTH_PX}px`;

  return (
    <AnimatePresence initial={false}>
      {isOpen && (
        <motion.div
          animate={{ width: targetWidth, opacity: 1 }}
          data-testid="ai-shell-panel"
          exit={{ width: 0, opacity: 0 }}
          initial={{ width: 0, opacity: 0 }}
          key="ai-shell-overlay"
          style={{
            position: 'fixed',
            top: 0,
            right: 0,
            height: '100vh',
            backgroundColor: '#fff',
            boxShadow: '-2px 0 8px rgba(0, 0, 0, 0.06)',
            zIndex: 1010, // Ensure shell appears above other content but below modals
            overflow: 'hidden',
          }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
        >
          <AIShellErrorBoundary>
            <Suspense
              fallback={
                <QStack sx={{ height: '100%', alignItems: 'center', justifyContent: 'center' }}>
                  <QLoader />
                </QStack>
              }
            >
              <AIShell />
            </Suspense>
          </AIShellErrorBoundary>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const computeMainWidth = (isOpen: boolean, isExpanded: boolean, shellWidth: string): string => {
  if (!isOpen) return '100%';
  if (isExpanded) return '0%';
  return `calc(100% - ${shellWidth})`;
};

const MainContent = ({ children }: PropsWithChildren<{}>) => {
  const { isOpen, isExpanded } = useAIShellContext();
  const shellWidth = isExpanded ? `${EXPANDED_WIDTH_VW}vw` : `${COLLAPSED_WIDTH_PX}px`;

  useEffect(() => {
    if (!isOpen || !isExpanded) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen, isExpanded]);

  return (
    <QBox
      sx={{
        width: computeMainWidth(isOpen, isExpanded, shellWidth),
        transition: 'width 0.2s ease-out',
        '@media (max-width: 768px)': {
          width: isOpen ? '0%' : '100%',
        },
      }}
    >
      {children}
    </QBox>
  );
};

export type AIShellFeatures = {
  multipleChoice?: boolean;
  citationChips?: boolean;
  feedback?: boolean;
  isInternal?: boolean;
  promptExperiments?: boolean;
};

type AIShellWrapperProps = PropsWithChildren<{
  userService: UserService;
  apiService: APIService;
  /**
   * Context detection adapter for URL-based context extraction.
   * Required for context-specific agents to be filtered correctly.
   */
  contextDetection?: IContextDetection;
  features?: AIShellFeatures;
}>;

export const AIShellWrapper = ({
  children,
  userService,
  apiService,
  contextDetection,
  features,
}: AIShellWrapperProps) => {
  return (
    <QStack direction="row" sx={{ justifyContent: 'stretch' }}>
      <AIShellContextProvider
        apiService={apiService}
        contextDetection={contextDetection}
        features={features}
        userService={userService}
      >
        <MainContent>{children}</MainContent>
        <AIShellAnimated />
      </AIShellContextProvider>
    </QStack>
  );
};
